import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { money } from "../tenant/tenantLabels";
import {
  fieldClass,
  labelClass,
  primaryButton,
  secondaryButton,
  smallButton,
} from "../template/templateLabels";

// What the Plan page works the incomes out from (the backend's utils/plan.js): the
// costs of every month, the share of income the variable costs take, how many months
// bring income, the profit wanted and the number of kiosks. Saved all at once.

// As the backend checks them
const COSTS_MAX = 20;
const NAME_MAX = 60;
const AMOUNT_MAX = 10000000000;
const VARIABLE_MAX = 90;
const KIOSKS_MAX = 1000;

// The form as the fields hold it: the numbers as text
const formOf = (settings) => ({
  costs: settings.costs.map(({ name, amount }) => ({ name, amount: String(amount) })),
  variablePercent: String(settings.variablePercent),
  seasonMonths: String(settings.seasonMonths),
  profit: String(settings.profit),
  // 0 is not set yet
  kiosks: settings.kiosks > 0 ? String(settings.kiosks) : "",
});

// A field's number from min to max, or null; whole asks for a whole number
const numberOf = (value, min, max, whole) => {
  const text = String(value).trim();
  const number = Number(text);
  return text !== "" &&
    Number.isFinite(number) &&
    number >= min &&
    number <= max &&
    (!whole || Number.isInteger(number))
    ? number
    : null;
};

// What is sent. The rows left empty are not.
const bodyOf = (form) => ({
  costs: form.costs
    .filter(({ name, amount }) => name.trim() || amount.trim())
    .map(({ name, amount }) => ({
      name: name.trim(),
      amount: numberOf(amount, 1, AMOUNT_MAX, true),
    })),
  variablePercent: numberOf(form.variablePercent, 0, VARIABLE_MAX),
  seasonMonths: numberOf(form.seasonMonths, 1, 12),
  profit: numberOf(form.profit, 0, AMOUNT_MAX, true),
  kiosks: numberOf(form.kiosks, 1, KIOSKS_MAX, true),
});

// The backend refuses these too; here the button says so before
const problemOf = (body) => {
  if (body.costs.some((cost) => !cost.name)) return "Зардал бүрд нэрээ бичнэ үү";
  if (body.costs.some((cost) => cost.amount === null)) {
    return "Зардал бүрийн дүнг төгрөгөөр, бүхэл тоогоор оруулна уу";
  }
  if (body.variablePercent === null) return `Хувьсах зардал 0-${VARIABLE_MAX}% байна`;
  if (body.seasonMonths === null) return "Орлоготой сар 1-12 байна";
  if (body.profit === null) return "Ашгаа төгрөгөөр, бүхэл тоогоор оруулна уу";
  if (body.kiosks === null) return "Киоскийн тоогоо оруулна уу";
  return null;
};

const numberClass =
  "block w-full h-10 pl-3 pr-12 text-sm text-gray-700 tabular-nums bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";
const removeButton =
  "flex items-center justify-center flex-shrink-0 w-10 h-10 text-gray-400 rounded-lg hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200";

const NumberField = ({ label, unit, help, value, onChange, ...input }) => (
  <label className="block">
    <span className={labelClass}>{label}</span>
    <span className="relative block">
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={numberClass}
        {...input}
      />
      <span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-3">
        {unit}
      </span>
    </span>
    <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">{help}</span>
  </label>
);

const PlanForm = ({ settings, onSave, onDirty }) => {
  const [form, setForm] = useState(() => formOf(settings));
  const [saving, setSaving] = useState(false);

  // What was loaded or saved, which the backend has cleaned up
  const saved = useMemo(() => formOf(settings), [settings]);
  useEffect(() => {
    setForm(saved);
  }, [saved]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);

  const body = bodyOf(form);
  const problem = problemOf(body);
  const canSave = dirty && !saving && !problem;
  const costsTotal = body.costs.reduce((sum, cost) => sum + (cost.amount || 0), 0);

  const set = (field, value) => setForm({ ...form, [field]: value });
  const setCost = (index, field, value) =>
    set(
      "costs",
      form.costs.map((cost, i) => (i === index ? { ...cost, [field]: value } : cost))
    );

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave(body);
    setSaving(false);
  };

  return (
    <>
      <div className="p-5 space-y-6">
        <div>
          <p className={labelClass}>Сар бүрийн тогтмол зардал</p>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Орлого орсон эсэхээс үл хамааран жилийн турш сар бүр төлдөг зардал: цалин, түрээс,
            сервер, интернэт, унаа.
          </p>
          <div className="space-y-2">
            {form.costs.map((cost, index) => (
              <div key={index} className="flex gap-2">
                <input
                  value={cost.name}
                  onChange={(e) => setCost(index, "name", e.target.value)}
                  maxLength={NAME_MAX}
                  placeholder="Ажилтны цалин"
                  aria-label="Зардлын нэр"
                  className={fieldClass}
                />
                <span className="relative block flex-shrink-0 w-44">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    step="1"
                    value={cost.amount}
                    onChange={(e) => setCost(index, "amount", e.target.value)}
                    placeholder="1500000"
                    aria-label="Сарын дүн"
                    className={numberClass}
                  />
                  <span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-3">
                    ₮
                  </span>
                </span>
                <button
                  type="button"
                  className={removeButton}
                  onClick={() =>
                    set(
                      "costs",
                      form.costs.filter((_, i) => i !== index)
                    )
                  }
                  aria-label={(cost.name || "Зардал") + " хасах"}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
            <button
              type="button"
              className={
                smallButton +
                (form.costs.length >= COSTS_MAX ? " opacity-50 cursor-not-allowed" : "")
              }
              disabled={form.costs.length >= COSTS_MAX}
              onClick={() => set("costs", [...form.costs, { name: "", amount: "" }])}
            >
              <i className="mr-1.5 fas fa-plus" />
              Зардал нэмэх
            </button>
            <p className="text-sm text-gray-600 tabular-nums dark:text-gray-300">
              Нийт: <b>{money(costsTotal)}</b> / сар
            </p>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <NumberField
            label="Хувьсах зардал"
            unit="%"
            min="0"
            max={VARIABLE_MAX}
            step="1"
            value={form.variablePercent}
            onChange={(value) => set("variablePercent", value)}
            help="Орлогын хэдэн хувь нь цаас, хор, төлбөрийн шимтгэл, засварт явдаг."
          />
          <NumberField
            label="Жилд орлоготой сар"
            unit="сар"
            min="1"
            max="12"
            step="0.5"
            value={form.seasonMonths}
            onChange={(value) => set("seasonMonths", value)}
            help="Зуны амралтаар орлого бараг ордоггүй. Үлдсэн сарууд бүтэн жилийн зардлыг нөхнө."
          />
          <NumberField
            label="Сард үлдээх ашиг"
            unit="₮"
            min="0"
            step="1"
            value={form.profit}
            onChange={(value) => set("profit", value)}
            help="Орлоготой сард бүх зардлын дараа үлдэх ёстой дүн. 0 бол зорилт нь алдагдалгүй түвшин."
          />
          <NumberField
            label="Киоскийн тоо"
            unit="ш"
            min="1"
            max={KIOSKS_MAX}
            step="1"
            value={form.kiosks}
            onChange={(value) => set("kiosks", value)}
            help="Зорилтыг энэ тоонд хувааж, киоск тус бүрийн зорилтыг гаргана."
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {problem && dirty ? (
            <span className="text-red-600 dark:text-red-400">
              <i className="mr-2 fas fa-exclamation-circle" />
              {problem}
            </span>
          ) : settings.updatedAt ? (
            <>
              <i className="mr-2 fas fa-history" />
              Сүүлд {settings.updatedBy ? settings.updatedBy + " " : ""}
              {moment(settings.updatedAt).format("YYYY-MM-DD HH:mm")}-д хадгалсан
            </>
          ) : (
            "Хадгалаагүй байна"
          )}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className={secondaryButton + (dirty && !saving ? "" : " opacity-50 cursor-not-allowed")}
            onClick={() => setForm(saved)}
            disabled={!dirty || saving}
          >
            Буцаах
          </button>
          <button
            type="button"
            className={
              primaryButton +
              " inline-flex items-center" +
              (canSave ? "" : " opacity-50 cursor-not-allowed")
            }
            onClick={save}
            disabled={!canSave}
          >
            {saving && <i className="mr-2 fas fa-spinner fa-spin" />}
            Хадгалах
          </button>
        </div>
      </div>
    </>
  );
};

NumberField.propTypes = {
  label: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

PlanForm.propTypes = {
  settings: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onDirty: PropTypes.func.isRequired,
};

export default PlanForm;
